/** @jsxImportSource @emotion/react */

import { batch, useDispatch, useSelector } from 'react-redux';
import SupplierCardComponent from '@SupplierCard';
import AnimationWrapper from '@AnimationWrapper';
import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';
import {
  clearConstraintsValidations,
  selectOptionAndUpdateValue,
  setComponentValidationStatus,
  setOptions,
  setSelectedOptions,
  updateValue,
} from '../../Lib/redux/slices/components';
import SubmissionStatus from '../../Data/SubmissionStatus';
import {
  setNavigationBlockOnPage,
  setShouldNavigateForward,
} from '../../Lib/redux/slices/navigationControls';
import { NAVIGATION_BLOCKS } from '../../Data/Constants';
import { hashString } from '../../Utils/utils';
import {
  updateIsLegalStatementRevealed,
  updateShouldRenderLegalStatement,
} from '../../Lib/redux/slices/legalStatement';
import { handleAnswerQuestionSideEffectsSaga } from '../../Hooks/actionCreators';
import { getOptionAnswerValueById } from '../CheckboxInput/checkboxInput.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { getSupplierNameToDisplay } from '../../Helpers/supplierCard';
import {
  buildSelectAndConnectShownMatches,
  resolveSuppliers,
} from '../../Helpers/supplierResolution';
import { exposeSelectAndConnectShown } from '../../Helpers/events';
import { reportWhereMissingUsps } from '../../Helpers/reportWhereMissingUsps';
import { sendSelectAndConnectShownEventToGateway } from '../../Services/Gateway/storeSelectAndConnectShownEvent';

const SelectAndConnect = ({ componentId, iFrameId, formId }) => {
  const components = useSelector((store) => store.components);
  const { selectAndConnectBrands } = useSelector((store) => store.submission);
  const component = components[`component_${componentId}`];

  const dynamicSelectAndConnect = useSelector(
    (store) => store.features?.dynamicSelectAndConnect
  );
  const dynamicMatches = useSelector(
    (store) => store.supplierMatches?.dynamicMatches
  );
  const visitorId = useSelector((store) => store.userIdentities?.visitorId);
  const subcategoryId = useSelector((store) => store.formConfig?.subcategoryId);
  const testMode = useSelector((store) => store.optionalFormConfig?.testMode);

  const additionalSettings = component?.component?.additionalSettings;

  const suppliers = resolveSuppliers({
    dynamicSelectAndConnect,
    dynamicMatches,
    additionalSettings,
  });

  const hasEmittedShownEvent = useRef(false);

  useEffect(() => {
    if (hasEmittedShownEvent.current) return;
    if (!suppliers) return;

    hasEmittedShownEvent.current = true;
    const matches = buildSelectAndConnectShownMatches({
      dynamicSelectAndConnect,
      dynamicMatches,
      additionalSettings,
    });
    exposeSelectAndConnectShown({
      iFrameId,
      formId,
      visitorId,
      matches,
    });
    sendSelectAndConnectShownEventToGateway({
      visitorId,
      formId,
      subcategoryId,
      matches,
      testMode,
    });
    // suppliers holds the secondaries only, already resolved - SupplierCard
    // renders the primary match and reports that one itself.
    reportWhereMissingUsps({
      dynamicSelectAndConnect,
      formId,
      subcategoryId,
      cards: suppliers,
    });
  }, [
    suppliers,
    visitorId,
    iFrameId,
    formId,
    subcategoryId,
    testMode,
    dynamicSelectAndConnect,
    dynamicMatches,
    additionalSettings,
  ]);

  const animationExperimentName = useGetExperimentName({
    componentId,
    componentName: 'AnimationWrapper',
  });
  const supplierCardExperimentName = useGetExperimentName({
    componentId,
    componentName: 'SupplierCard',
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (!suppliers || component.input?.options) return;

    const options = suppliers.map((supplier) => ({
      id: supplier.supplier_id,
      key: supplier.brandId,
      text: supplier.brandName,
    }));

    const selectedOptions = suppliers
      .filter((supplier) => supplier.selected_state)
      .map((supplier) => supplier.supplier_id);

    const selectedOptionValue = selectedOptions
      .map((optionId) => {
        return getOptionAnswerValueById(optionId, options);
      })
      .join();

    batch(() => {
      dispatch(
        setOptions({
          componentId,
          options,
        })
      );
      dispatch(
        setSelectedOptions({
          componentId,
          selectedOptions,
        })
      );
      dispatch(updateValue({ componentId, value: selectedOptionValue }));
    });
  }, [suppliers, component.input?.options, dispatch, componentId]);

  useEffect(() => {
    batch(() => {
      dispatch(
        updateShouldRenderLegalStatement({
          shouldRenderLegalStatement: true,
        })
      );

      dispatch(
        updateIsLegalStatementRevealed({
          isLegalStatementRevealed: true,
        })
      );
    });
  }, [dispatch]);

  useEffect(() => {
    if (
      selectAndConnectBrands &&
      Object.values(selectAndConnectBrands).length > 0 &&
      Object.values(selectAndConnectBrands).every(
        (brand) => brand.submissionStatus === SubmissionStatus.COMPLETE
      )
    ) {
      dispatch(
        setNavigationBlockOnPage({
          pageNumber: component.component.page,
          blockName: NAVIGATION_BLOCKS.SELECT_AND_CONNECT,
          isPreventingNavigation: false,
        })
      );
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [selectAndConnectBrands, dispatch, component.component.page]);

  const onClick = (event) => {
    const supplierId = event.id;
    const newSelectedState = !event.selected;

    batch(() => {
      dispatch(clearConstraintsValidations({ componentId }));
      dispatch(
        selectOptionAndUpdateValue({
          componentId,
          optionId: supplierId,
          selected: newSelectedState,
        })
      );
      dispatch(
        setComponentValidationStatus({
          componentId,
          iFrameId,
          formId,
        })
      );
      dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
    });
  };

  if (!suppliers) return null;

  const supplierCards = suppliers.map((supplier, index) => {
    const SupplierLogo = (
      <img
        alt="supplier-logo"
        data-testid={`${supplier.supplier_id}-${index}`}
        src={supplier.brandLogoUrl}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    );

    return (
      <AnimationWrapper
        key={`${supplier.supplier_id}`}
        effect="fadeInUp"
        delay={400}
        animationTime={600}
        experimentName={animationExperimentName}
      >
        <div style={{ marginBottom: 16 }}>
          <SupplierCardComponent
            key={`${supplier.supplier_id}`}
            badgeText={supplier.badge_label}
            SupplierLogo={supplier.brandLogoUrl ? SupplierLogo : undefined}
            supplierName={getSupplierNameToDisplay({
              brandLogoUrl: supplier.brandLogoUrl,
              brandName: supplier.brandName,
              shouldDisplayBrandName: supplier.display_brand_name, // need to hide the brand name on this feature here and on the select and connect component
            })}
            ctaText={supplier.cta_text || 'Request a Quote'}
            id={supplier.supplier_id}
            onClick={onClick}
            invertCtaColors={supplier.cta_button_style === 'outlined'}
            includeCheckbox
            experimentName={supplierCardExperimentName}
            selected={component.input.selectedOptions?.includes(
              supplier.supplier_id
            )}
          >
            <ul>
              {supplier.usps.map((usp, i) => (
                <li
                  key={`usp-${componentId}-${supplier.supplier_id}-${hashString(
                    usp,
                    i
                  )}`}
                >
                  {usp}
                </li>
              ))}
            </ul>
          </SupplierCardComponent>
        </div>
      </AnimationWrapper>
    );
  });

  return <Box marginTop={2}>{supplierCards}</Box>;
};

export default SelectAndConnect;
